/**
 * Translation safety.
 *
 * Machine translation reaches no patient unreviewed — that gate lives in the
 * workflow. This module is the other control: a deterministic check that the
 * values which cause harm when wrong survived translation unchanged.
 *
 * The demonstration checked six fixed values. Here they are extracted from the
 * source text at run time, because a real letter has whatever values it has.
 */

export const LANGUAGES = [
  { code: 'pl', name: 'Polish', native: 'Polski', rtl: false },
  { code: 'ur', name: 'Urdu', native: 'اردو', rtl: true },
  { code: 'bn', name: 'Bengali', native: 'বাংলা', rtl: false },
  { code: 'ar', name: 'Arabic', native: 'العربية', rtl: true },
  { code: 'ro', name: 'Romanian', native: 'Română', rtl: false },
  { code: 'so', name: 'Somali', native: 'Soomaali', rtl: false },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી', rtl: false },
  { code: 'zh', name: 'Mandarin', native: '中文', rtl: false },
  { code: 'pt', name: 'Portuguese', native: 'Português', rtl: false },
  { code: 'tr', name: 'Turkish', native: 'Türkçe', rtl: false },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

export const LANGUAGE_BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l])) as Record<
  LanguageCode,
  (typeof LANGUAGES)[number]
>;

export const TRANSLATION_STATUS = ['draft', 'reviewed', 'approved', 'stale', 'blocked'] as const;
export type TranslationStatus = (typeof TRANSLATION_STATUS)[number];

/**
 * Numerals change form across scripts. Eastern Arabic numerals in Arabic and
 * Urdu, Devanagari in some Gujarati contexts, full-width in Chinese. Without
 * normalising first, every RTL translation fails the check spuriously.
 */
const NUMERAL_MAPS: Record<string, string> = {};
function addNumerals(start: number) {
  for (let i = 0; i < 10; i++) {
    NUMERAL_MAPS[String.fromCodePoint(start + i)] = String(i);
  }
}
addNumerals(0x0660); // Arabic-Indic
addNumerals(0x06f0); // Extended Arabic-Indic (Urdu, Persian)
addNumerals(0x0966); // Devanagari
addNumerals(0x09e6); // Bengali
addNumerals(0x0ae6); // Gujarati
addNumerals(0xff10); // Full-width

export function normaliseNumerals(s: string): string {
  return s.replace(/./gu, (ch) => NUMERAL_MAPS[ch] ?? ch);
}

export type CriticalKind = 'date-numeral' | 'time' | 'telephone' | 'room' | 'organisation' | 'year';

export interface CriticalValue {
  kind: CriticalKind;
  label: string;
  value: string;
}

export interface CriticalCheck extends CriticalValue {
  present: boolean;
}

const TIME = /\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/g;
const YEAR = /\b(20\d{2})\b/g;
const DAY_OF_MONTH = /\b([1-9]|[12]\d|3[01])(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\b/gi;
const TELEPHONE = /\b0\d{2,4}[\s-]?\d{3,4}[\s-]?\d{3,4}\b/g;
const ROOM = /\broom\s+([\w-]+)\b/gi;

/**
 * Pull the values out of the approved English source that must survive
 * translation. Organisation names cannot be pattern-matched, so they are
 * passed in — the practice name is known from the tenant.
 */
export function extractCriticalValues(source: string, organisationNames: string[] = []): CriticalValue[] {
  const out: CriticalValue[] = [];
  const seen = new Set<string>();
  const push = (kind: CriticalKind, label: string, value: string) => {
    const key = `${kind}:${value}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind, label, value });
  };

  for (const m of source.matchAll(TIME)) push('time', 'Time', m[0]);
  for (const m of source.matchAll(YEAR)) if (m[1]) push('year', 'Year', m[1]);
  for (const m of source.matchAll(DAY_OF_MONTH)) if (m[1]) push('date-numeral', 'Day of month', m[1]);
  for (const m of source.matchAll(TELEPHONE)) push('telephone', 'Telephone', m[0].replace(/\s+/g, ' ').trim());
  for (const m of source.matchAll(ROOM)) if (m[1]) push('room', 'Room number', m[1]);
  for (const name of organisationNames) {
    if (source.includes(name)) push('organisation', 'Organisation name', name);
  }

  return out;
}

/**
 * Weekday and month names SHOULD be translated — the demonstration was explicit
 * about that, and a Polish letter saying "Tuesday" helps nobody. Only the
 * numerals, telephone number and organisation name carry across unchanged.
 */
export function checkCriticalValues(translated: string, values: CriticalValue[]): CriticalCheck[] {
  const haystack = normaliseNumerals(translated);
  const digitsOnly = (s: string) => s.replace(/\D/g, '');

  return values.map((v) => {
    let present: boolean;
    if (v.kind === 'telephone') {
      // Separators legitimately change; the digits must not.
      present = digitsOnly(haystack).includes(digitsOnly(v.value));
    } else if (v.kind === 'organisation') {
      present = translated.includes(v.value);
    } else {
      present = haystack.includes(normaliseNumerals(v.value));
    }
    return { ...v, present };
  });
}

export function criticalCheckPassed(checks: CriticalCheck[]): boolean {
  return checks.every((c) => c.present);
}
